import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ APPYMT CREATE CONFIGURATION ============
const APPYMT_DETAIL_PREFIX = 'APPYMTDETAILS.';

const isDetailField = (key) => key.toUpperCase().startsWith(APPYMT_DETAIL_PREFIX);

// APPYMTDETAILS.APPYMTDETAIL.RECORDKEY → RECORDKEY
const getDetailSubKey = (key) => key.split('.').pop();

// ============ DETECTION ============
export const isAppPaymentCreate = (objectName, mode) => {
  return objectName?.toUpperCase() === 'APPYMT' && mode === 'create';
};

// ============ FUNCTIONS ============
export const getAppPaymentCreateSummary = (records) => {
  const grouped = {};
  const missingDocNo = [];
  const vendorConflicts = [];

  records.forEach((rec, idx) => {
    const docNo = String(rec.DOCNUMBER ?? '').trim();
    if (!docNo) { missingDocNo.push(idx + 2); return; }

    if (!grouped[docNo]) {
      grouped[docNo] = { count: 0, vendorId: String(rec.VENDORID ?? '').trim() };
    } else {
      const incomingVendor = String(rec.VENDORID ?? '').trim();
      if (grouped[docNo].vendorId !== incomingVendor) {
        const msg = `DOCNUMBER "${docNo}" has conflicting VENDORID values ("${grouped[docNo].vendorId}" vs "${incomingVendor}")`;
        if (!vendorConflicts.includes(msg)) vendorConflicts.push(msg);
      }
    }
    grouped[docNo].count++;
  });

  return {
    payments: Object.entries(grouped).map(([docNo, info]) => ({ docNo, lineCount: info.count })),
    missingDocNoRows: missingDocNo,
    vendorConflicts,
    totalLines: records.length,
    totalPayments: Object.keys(grouped).length
  };
};

export const buildAppPaymentPreviewXml = (records) => {
  const grouped = {};
  records.forEach(rec => {
    const docNo = String(rec.DOCNUMBER ?? '').trim();
    if (!docNo) return;
    if (!grouped[docNo]) grouped[docNo] = [];
    grouped[docNo].push(rec);
  });

  const docNos = Object.keys(grouped);
  if (docNos.length === 0) return '<!-- No valid records with DOCNUMBER -->';

  const firstDocNo = docNos[0];
  const firstGroupRecords = grouped[firstDocNo];
  const firstRec = firstGroupRecords[0];

  const headerXml = Object.entries(firstRec)
    .filter(([k]) => !isDetailField(k))
    .map(([k, v]) => `      <${k}>${escapeXml(String(v))}</${k}>`)
    .join('\n');

  const sampleRows = firstGroupRecords.slice(0, 2);
  const detailsXml = sampleRows.map(rec => {
    const fields = Object.entries(rec)
      .filter(([k]) => isDetailField(k))
      .map(([k, v]) => `          <${getDetailSubKey(k)}>${escapeXml(String(v))}</${getDetailSubKey(k)}>`)
      .join('\n');
    return `        <APPYMTDETAIL>\n${fields}\n        </APPYMTDETAIL>`;
  }).join('\n');

  const moreDetailsNote = firstGroupRecords.length > 2
    ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more detail(s) ... -->` : '';
  const morePaymentsNote = docNos.length > 1
    ? `\n\n<!-- ... and ${docNos.length - 1} more payment(s) will be created ... -->` : '';

  return `<function controlid="${escapeXml(firstDocNo)}">
  <create>
    <APPYMT>
${headerXml}
      <APPYMTDETAILS>
${detailsXml}${moreDetailsNote}
      </APPYMTDETAILS>
    </APPYMT>
  </create>
</function>${morePaymentsNote}`;
};

export const createAppPayments = async (config, records) => {
  const grouped = {};
  records.forEach(rec => {
    const docNo = String(rec.DOCNUMBER ?? '').trim();
    if (!docNo) return;
    if (!grouped[docNo]) grouped[docNo] = [];
    grouped[docNo].push(rec);
  });

  const functions = Object.entries(grouped).map(([docNo, rows]) => {
    const headerXml = Object.entries(rows[0])
      .filter(([k]) => !isDetailField(k))
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('');

    const detailsXml = rows.map(rec => {
      const fields = Object.entries(rec)
        .filter(([k]) => isDetailField(k))
        .map(([k, v]) => `<${getDetailSubKey(k)}>${escapeXml(String(v))}</${getDetailSubKey(k)}>`)
        .join('');
      return `<APPYMTDETAIL>${fields}</APPYMTDETAIL>`;
    }).join('');

    return `<function controlid="${escapeXml(String(docNo))}"><create><APPYMT>${headerXml}<APPYMTDETAILS>${detailsXml}</APPYMTDETAILS></APPYMT></create></function>`;
  }).join('');

  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, 'APPYMT', xmlPayload, 'APPYMT CREATE');
  return result.results;
};
